import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { invitationService } from '@/services/api/invitation.service';
import type { InvitationCreateRequest } from '@/types/invitation';
import { isSubscriptionError } from '@/lib/subscription-error-handler';
import { sanitizeErrorMessage } from '@/lib/error-utils';

/**
 * Hook to fetch invitations list
 */
export function useInvitations(status?: string) {
  return useQuery({
    queryKey: ['invitations', status],
    queryFn: () => invitationService.getInvitations(status)
  });
}

/**
 * Hook to create a new invitation
 */
export function useCreateInvitation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('invitations');

  return useMutation({
    mutationFn: (data: InvitationCreateRequest) => invitationService.createInvitation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', 'limits'] });
      toast({
        title: t('toasts.sentTitle'),
        description: t('toasts.sentDescription')
      });
    },
    onError: (error: any) => {
      // Don't show toast for subscription errors - let the page handle them with UpgradeDialog
      if (isSubscriptionError(error)) {
        return; // Skip toast, error will be caught by page-level handler
      }

      // Show sanitized error message for other errors
      toast({
        title: t('toasts.sendErrorTitle'),
        description: sanitizeErrorMessage(error, t('toasts.sendErrorDescription')),
        variant: 'destructive'
      });
    }
  });
}

/**
 * Hook to resend an invitation
 */
export function useResendInvitation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('invitations');

  return useMutation({
    mutationFn: (id: number) => invitationService.resendInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      toast({
        title: t('toasts.resentTitle'),
        description: t('toasts.resentDescription')
      });
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.resendErrorTitle'),
        description: sanitizeErrorMessage(error, t('toasts.resendErrorDescription')),
        variant: 'destructive'
      });
    }
  });
}

/**
 * Hook to revoke an invitation
 */
export function useRevokeInvitation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('invitations');

  return useMutation({
    mutationFn: (id: number) => invitationService.revokeInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      toast({
        title: t('toasts.revokedTitle'),
        description: t('toasts.revokedDescription')
      });
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.revokeErrorTitle'),
        description: sanitizeErrorMessage(error, t('toasts.revokeErrorDescription')),
        variant: 'destructive'
      });
    }
  });
}

/**
 * Hook to delete an invitation
 */
export function useDeleteInvitation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('invitations');

  return useMutation({
    mutationFn: (id: number) => invitationService.deleteInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', 'limits'] });
      toast({
        title: t('toasts.deletedTitle'),
        description: t('toasts.deletedDescription')
      });
    },
    onError: (error: any) => {
      toast({
        title: t('toasts.deleteErrorTitle'),
        description: sanitizeErrorMessage(error, t('toasts.deleteErrorDescription')),
        variant: 'destructive'
      });
    }
  });
}
